import React from 'react';
import { X, Tag, Trash2 } from 'lucide-react';

const FilterBottomSheet = ({
  isOpen,
  onClose,
  selectedCategory,
  setSelectedCategory,
  cookedFilter,
  setCookedFilter,
  selectedLabel,
  setSelectedLabel,
  userLabels = [],
  onManageLabels,
  showDeleted,
  setShowDeleted,
}) => {
  if (!isOpen) return null;

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedCategory !== 'all') count++;
    if (cookedFilter !== 'all') count++;
    if (selectedLabel !== 'all') count++;
    if (showDeleted) count++;
    return count;
  };

  const handleClearAll = () => {
    setSelectedCategory('all');
    setCookedFilter('all');
    setSelectedLabel('all');
    setShowDeleted(false);
  };

  const handleApply = () => {
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />

      {/* Bottom Sheet - account for bottom nav (64px) */}
      <div className="fixed inset-x-0 bottom-16 z-50 bg-white rounded-t-2xl shadow-xl flex flex-col" style={{ maxHeight: 'calc(80vh - 64px)' }}>
        {/* Sticky Header */}
        <div className="flex-shrink-0">
          {/* Drag Handle */}
          <div className="flex justify-center py-3">
            <div className="w-12 h-1 bg-gray-300 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
            <h3 className="text-lg font-semibold">Filters</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 min-h-0">
          {/* Category Filter */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Category</h4>
            <div className="space-y-2">
              {[
                { value: 'all', label: 'All Categories' },
                { value: 'main_dish', label: 'Main Dishes' },
                { value: 'appetizer', label: 'Appetizers' },
                { value: 'dessert', label: 'Desserts' },
                { value: 'salad', label: 'Salads' },
                { value: 'soup', label: 'Soups & Stews' },
                { value: 'side', label: 'Sides' },
                { value: 'breakfast', label: 'Breakfast & Brunch' },
                { value: 'beverage', label: 'Beverages' },
                { value: 'other', label: 'Other' },
              ].map((cat) => (
                <label
                  key={cat.value}
                  className="flex items-center gap-3 py-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.value}
                    checked={selectedCategory === cat.value}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-5 h-5 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-gray-700">{cat.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Label Filter */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Label</h4>
              {onManageLabels && (
                <button
                  onClick={onManageLabels}
                  className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
                >
                  <Tag size={12} />
                  Manage
                </button>
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-3 py-2 cursor-pointer">
                <input
                  type="radio"
                  name="label"
                  value="all"
                  checked={selectedLabel === 'all'}
                  onChange={(e) => setSelectedLabel(e.target.value)}
                  className="w-5 h-5 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-gray-700">All Labels</span>
              </label>
              {userLabels.map((label) => (
                <label
                  key={label.name}
                  className="flex items-center gap-3 py-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="label"
                    value={label.name}
                    checked={selectedLabel === label.name}
                    onChange={(e) => setSelectedLabel(e.target.value)}
                    className="w-5 h-5 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="flex items-center gap-2 text-gray-700">
                    {label.icon ? (
                      <span>{label.icon}</span>
                    ) : (
                      <span 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: label.color }}
                      />
                    )}
                    {label.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Status</h4>
            <div className="space-y-2">
              {[
                { value: 'all', label: 'All Recipes' },
                { value: 'cooked', label: 'Cooked' },
                { value: 'not_cooked', label: 'Not Cooked Yet' },
              ].map((status) => (
                <label
                  key={status.value}
                  className="flex items-center gap-3 py-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="cooked"
                    value={status.value}
                    checked={cookedFilter === status.value}
                    onChange={(e) => setCookedFilter(e.target.value)}
                    className="w-5 h-5 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-gray-700">{status.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Show Deleted Option */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Other</h4>
            <label className="flex items-center gap-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="w-5 h-5 rounded text-amber-600 focus:ring-amber-500"
              />
              <Trash2 size={18} className="text-gray-400" />
              <span className="text-gray-700">Show Deleted</span>
            </label>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex-shrink-0 flex gap-3 px-4 py-4 border-t bg-gray-50">
          <button
            onClick={handleClearAll}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={handleApply}
            data-testid="apply-filters-button"
            className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors"
          >
            Apply {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          </button>
        </div>
      </div>
    </>
  );
};

export default FilterBottomSheet;